import { monthName } from '../lib/i18n/format'
import type { StaffKey } from '../lib/staffText'

/** A dateTime element's patterns, mirroring internal/layout/datetime.go. Absent is the first. */
export const DATE_FORMATS: ReadonlyArray<{ value: string; label: StaffKey }> = [
  { value: 'dd.MM.yyyy HH:mm', label: 'designs.insp.dateAndTime' },
  { value: 'dd.MM.yyyy', label: 'designs.insp.dateOnly' },
  { value: 'HH:mm', label: 'designs.insp.timeOnly' },
]

/** A screen's Date: "1 November 2026", the month named in the screen's language. */
export const LONG_DATE = 'd MMMM yyyy'

/** A screen's patterns: the long date first, then the paper ones. */
export const SCREEN_DATE_FORMATS: readonly string[] = [LONG_DATE, 'dd.MM.yyyy', 'HH:mm', 'dd.MM.yyyy HH:mm']

export const DEFAULT_DATE_FORMAT = DATE_FORMATS[0]!.value
const MAX_DATE_FORMAT = 32
const TOKENS = ['yyyy', 'yy', 'MM', 'dd', 'HH', 'mm', 'ss'] as const
// Screens only (layout.screenDateTokens): the month's name and the day without a zero.
const SCREEN_TOKENS = ['MMMM', ...TOKENS, 'd'] as const
const SEPARATORS = ' .:/-,'

type Token = (typeof SCREEN_TOKENS)[number]

/** The pattern split into tokens and separators, or null where the server refuses it. */
function parse(format: string, screen: boolean): Array<{ token: Token } | { literal: string }> | null {
  if (format.length > MAX_DATE_FORMAT) return null
  const tokens: readonly Token[] = screen ? SCREEN_TOKENS : TOKENS
  const parts: Array<{ token: Token } | { literal: string }> = []
  let rest = format
  let found = 0
  while (rest) {
    const token = tokens.find((t) => rest.startsWith(t))
    if (token) {
      parts.push({ token })
      rest = rest.slice(token.length)
      // "MMMMM" or "ddd" is a longer token than any we print, not two.
      if (rest[0] === token[0]) return null
      found++
      continue
    }
    if (!SEPARATORS.includes(rest[0]!)) return null
    parts.push({ literal: rest[0]! })
    rest = rest.slice(1)
  }
  return found > 0 ? parts : null
}

/** The inspector's choices: the presets, plus a pattern set elsewhere (an agent, an import) shown as itself. */
export function dateFormatChoices(current: string | undefined): Array<{ value: string; label?: StaffKey }> {
  const choices: Array<{ value: string; label?: StaffKey }> = [...DATE_FORMATS]
  if (current && !DATE_FORMATS.some((f) => f.value === current)) choices.push({ value: current })
  return choices
}

/** A screen's choices, each shown as it draws now; a pattern set elsewhere is kept. */
export function screenDateChoices(current: string | undefined, at: Date, lang: string): Array<{ value: string; label: string }> {
  const values = [...SCREEN_DATE_FORMATS]
  if (current && !values.includes(current)) values.push(current)
  return values.map((value) => ({ value, label: formatDate(at, value, lang) }))
}

/** Whether a pattern shows the date, the time or both. */
export function dateFormatShows(format: string | undefined): 'date' | 'time' | 'both' {
  const parts = parse(format || DEFAULT_DATE_FORMAT, true) ?? []
  const date = parts.some((p) => 'token' in p && /^[yMd]/.test(p.token))
  const time = parts.some((p) => 'token' in p && /^[Hms]/.test(p.token))
  return date && !time ? 'date' : time && !date ? 'time' : 'both'
}

/** Whether the server takes the pattern: paper prints numbers only, a screen may name the month. */
export function validDateFormat(format: string | undefined, screen = false): boolean {
  return !format || parse(format, screen) !== null
}

/**
 * The moment as the printer prints it; a refused pattern prints the default, as the agent does.
 * screenLang draws a screen's pattern, where the month is named in that language.
 */
export function formatDate(at: Date, format: string | undefined, screenLang?: string): string {
  const screen = screenLang !== undefined
  const parts = parse(format || DEFAULT_DATE_FORMAT, screen) ?? parse(DEFAULT_DATE_FORMAT, false)!
  const two = (n: number) => String(n).padStart(2, '0')
  const value: Record<Token, () => string> = {
    yyyy: () => String(at.getFullYear()),
    yy: () => two(at.getFullYear() % 100),
    // Our own tables for en/az/ru, as a date says it ("noyabr", "ноября").
    MMMM: () => monthName(screenLang || 'en', at.getMonth()),
    MM: () => two(at.getMonth() + 1),
    dd: () => two(at.getDate()),
    d: () => String(at.getDate()),
    HH: () => two(at.getHours()),
    mm: () => two(at.getMinutes()),
    ss: () => two(at.getSeconds()),
  }
  return parts.map((p) => ('token' in p ? value[p.token]() : p.literal)).join('')
}

/** A slip's date and time, offered as two components. */
export const DATE_PARTS: ReadonlyArray<{ format: string; label: StaffKey }> = [
  { format: 'dd.MM.yyyy', label: 'designs.el.date' },
  { format: 'HH:mm', label: 'designs.el.time' },
]

/** A screen's Date and Time: the month named, the time on a 24-hour clock. */
export const SCREEN_DATE_PARTS: ReadonlyArray<{ format: string; label: StaffKey }> = [
  { format: LONG_DATE, label: 'designs.el.date' },
  { format: 'HH:mm', label: 'designs.el.time' },
]
